import { Spinner } from "@/components/ui/spinner";
import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import {
  uploadBankStatement,
  MAX_STATEMENT_BYTES,
} from "@/services/gmailIntegrationApi";
import { ApiError } from "@/services/apiClient";
import { refreshSuggestions } from "@/features/accounts/store/useAccountStore";
import { useAppStore } from "../../store/useAppStore";

export default function StatementUploadButton() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so selecting the same file again re-triggers change.
    e.target.value = "";
    if (!file) return;

    if (file.size > MAX_STATEMENT_BYTES) {
      toast.error("File exceeds the 20 MB limit");
      return;
    }

    setUploading(true);
    try {
      const result = await uploadBankStatement(file);
      const description = result.warnings.length
        ? result.warnings.join(" ")
        : undefined;

      if (result.imported === 0) {
        toast.error("No transactions found in this file", { description });
      } else if (result.failed > 0) {
        toast.warning(
          `${result.imported} imported, ${result.failed} could not be parsed`,
          { description },
        );
      } else {
        const noun = result.imported === 1 ? "transaction" : "transactions";
        toast.success(`${result.imported} ${noun} imported`, { description });
      }

      if (result.imported > 0) {
        const { viewYear, viewMonth, loadMonth } = useAppStore.getState();
        await loadMonth(viewYear, viewMonth);
        await refreshSuggestions();
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Upload failed. Please try again.";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={handleSelect}
      />
      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        aria-label="Upload statement"
        className="inline-flex items-center justify-center gap-1.5 text-[12px] font-medium w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 rounded-[10px] transition-colors cursor-pointer bg-transparent text-[var(--text-secondary)] border border-border hover:bg-[var(--surface-muted)] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {uploading ? (
          <Spinner className="size-3.5" />
        ) : (
          <Upload className="w-3.5 h-3.5" />
        )}
        <span className="hidden sm:inline">
          {uploading ? "Uploading…" : "Upload statement"}
        </span>
      </button>
    </>
  );
}
